import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { MediaPicker } from "@/components/MediaPicker";
import PublicationDetailSheet from "@/components/PublicationDetailSheet";
import { formatTenantDateTime } from "@/lib/datetime";
import { Loader2, Plus, CalendarClock, X, Facebook, Instagram, Twitter, AtSign, AlertTriangle, Home, ExternalLink, CheckCheck, Ban, Linkedin, Bot, Music2, Send } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const CHAR_LIMITS = { facebook: 63206, instagram: 2200, twitter: 280, threads: 500, nextdoor: 3500, linkedin: 3000, tiktok: 4000 };
const PLATFORM_ICON = { facebook: Facebook, instagram: Instagram, twitter: Twitter, threads: AtSign, nextdoor: Home, linkedin: Linkedin, tiktok: Music2 };
const URL_RE = /https?:\/\/\S+/;
const ASSISTED_TOOLTIP = "Esta red no permite que otras herramientas publiquen automáticamente. La plataforma te deja el post listo y vos lo publicás con un toque.";
const ASSISTED_PLATFORMS = new Set(["nextdoor", "tiktok"]);
const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const RUN_RESULT_LABEL = {
  apagado: "Apagado — no se programó nada",
  sin_redes_conectadas: "Sin redes conectadas para las elegidas",
  sin_contenido_aprobado: "No hay contenido aprobado disponible",
  fuera_de_ventana: "Fuera de la franja horaria",
  dia_no_habilitado: "Hoy no es un día habilitado",
  limite_diario_alcanzado: "Límite diario alcanzado en todas las cuentas",
  asistidas_al_tope: "Tope de publicaciones asistidas pendientes alcanzado",
  error: "Falló la corrida — revisá los logs",
};
const runResultLabel = (result) => {
  if (!result) return null;
  if (result.startsWith("programadas:")) return `Se programaron ${result.split(":")[1]} publicación(es)`;
  return RUN_RESULT_LABEL[result] || result;
};

const STATUS = {
  draft: { label: "Borrador", cls: "bg-[#71717A]/10 text-[#71717A]" },
  scheduled: { label: "Programada", cls: "bg-[#0055FF]/10 text-[#0055FF]" },
  publishing: { label: "Publicando", cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  needs_verification: { label: "Verificando con Meta", cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  published: { label: "Publicada", cls: "bg-[#10B981]/10 text-[#10B981]" },
  failed: { label: "Falló", cls: "bg-[#EF4444]/10 text-[#EF4444]" },
  blocked: { label: "Bloqueada", cls: "bg-[#A1A1AA]/10 text-[#71717A]" },
  canceled: { label: "Cancelada", cls: "bg-[#A1A1AA]/10 text-[#71717A]" },
  needs_reschedule: { label: "Necesita reprogramar", cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  pending_manual: { label: "Asistida — la publicás vos", cls: "bg-[#8ED500]/15 text-[#4D7A00]", tooltip: ASSISTED_TOOLTIP },
  marked_published: { label: "Publicada (confirmada)", cls: "bg-[#10B981]/10 text-[#10B981]" },
  discarded: { label: "Descartada", cls: "bg-[#A1A1AA]/10 text-[#71717A]" },
  expired_unpublished: { label: "Vencida — no se publicó", cls: "bg-[#EF4444]/10 text-[#EF4444]", tooltip: ASSISTED_TOOLTIP },
};

export default function PublicationsPage() {
  const { activeTenantId, activeTenant, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ account_ids: [], text: "", media: null, scheduled_at: "", destination_type: "external", destination_url: "" });
  const [detailPubId, setDetailPubId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState(null);
  const [publisherConfig, setPublisherConfig] = useState(null);
  const [savingPublisherConfig, setSavingPublisherConfig] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const loadPublisherConfig = useCallback(async () => {
    const res = await api.get("/social/publisher-config");
    setPublisherConfig(res.data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pubs, accs] = await Promise.all([api.get("/social/publications"), api.get("/social/accounts")]);
      setItems(pubs.data);
      setAccounts(accs.data);
      loadPublisherConfig();
    } finally {
      setLoading(false);
    }
  }, [loadPublisherConfig]);

  useEffect(() => { if (activeTenantId) load(); }, [activeTenantId, load]);

  const toggleAccount = (id) => {
    setForm((f) => ({
      ...f,
      account_ids: f.account_ids.includes(id) ? f.account_ids.filter((x) => x !== id) : [...f.account_ids, id],
    }));
  };

  const selectedAccounts = accounts.filter((a) => form.account_ids.includes(a.id));
  const activeLimit = selectedAccounts.length
    ? Math.min(...selectedAccounts.map((a) => CHAR_LIMITS[a.platform] || 999999))
    : null;
  const needsImage = selectedAccounts.some((a) => a.platform === "instagram");
  const showXLinkCostWarning = selectedAccounts.some((a) => a.platform === "twitter") && URL_RE.test(form.text);

  const openCreate = () => {
    setForm({ account_ids: [], text: "", media: null, scheduled_at: "", destination_type: "external", destination_url: "" });
    setOpen(true);
  };

  const save = async () => {
    if (form.account_ids.length === 0) return toast.error("Elegí al menos una cuenta");
    if (!form.text.trim()) return toast.error("Escribí el texto de la publicación");
    if (!form.scheduled_at) return toast.error("Elegí fecha y hora");
    if (needsImage && !form.media) return toast.error("Instagram requiere una imagen");
    try {
      await api.post("/social/publications", {
        account_ids: form.account_ids, text: form.text,
        media: form.media ? [form.media] : [],
        scheduled_at: form.scheduled_at,
        destination_type: form.destination_type,
        destination_url: form.destination_type === "external" ? (form.destination_url || undefined) : undefined,
      });
      toast.success("Publicación programada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo programar");
    }
  };

  const cancel = async (pub) => {
    try {
      await api.delete(`/social/publications/${pub.id}`);
      toast.success("Publicación cancelada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cancelar");
    }
  };

  const markPublishedManual = async (pub) => {
    try {
      await api.post(`/social/publications/${pub.id}/mark-published-manual`);
      toast.success("Marcada como publicada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo confirmar");
    }
  };

  const discardManual = async (pub) => {
    try {
      await api.post(`/social/publications/${pub.id}/discard`);
      toast.success("Publicación descartada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descartar");
    }
  };

  const openScheduleDraft = (pub) => setScheduleDraft({ id: pub.id, text: pub.text, scheduled_at: "" });

  const needsRescheduleCount = items.filter((p) => p.status === "needs_reschedule").length;

  const saveScheduleDraft = async () => {
    if (!scheduleDraft.scheduled_at) return toast.error("Elegí fecha y hora");
    try {
      await api.post(`/social/publications/${scheduleDraft.id}/schedule`, {
        text: scheduleDraft.text, scheduled_at: scheduleDraft.scheduled_at,
      });
      toast.success("Publicación programada");
      setScheduleDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo programar");
    }
  };

  const toggleDay = (day) => {
    setPublisherConfig((c) => ({
      ...c,
      days_of_week: c.days_of_week.includes(day) ? c.days_of_week.filter((d) => d !== day) : [...c.days_of_week, day].sort(),
    }));
  };

  const togglePlatform = (platform) => {
    setPublisherConfig((c) => ({
      ...c,
      platforms: c.platforms.includes(platform) ? c.platforms.filter((p) => p !== platform) : [...c.platforms, platform],
    }));
  };

  const savePublisherConfig = async () => {
    setSavingPublisherConfig(true);
    try {
      const res = await api.put("/social/publisher-config", {
        enabled: publisherConfig.enabled, posts_per_day: publisherConfig.posts_per_day,
        window_start_hour_local: publisherConfig.window_start_hour_local,
        window_end_hour_local: publisherConfig.window_end_hour_local,
        platforms: publisherConfig.platforms, days_of_week: publisherConfig.days_of_week,
        assisted_pending_limit: publisherConfig.assisted_pending_limit,
      });
      setPublisherConfig({ ...res.data, assisted_status: publisherConfig.assisted_status });
      loadPublisherConfig();
      toast.success("Programador automático actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingPublisherConfig(false);
    }
  };

  const runPublisherNow = async () => {
    setRunningNow(true);
    try {
      const res = await api.post("/social/publisher-config/run-now", { tenant_id: activeTenantId });
      toast.success(runResultLabel(res.data.result) || "Corrida terminada");
      loadPublisherConfig();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo correr ahora");
    } finally {
      setRunningNow(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="publications-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A]">Publicaciones programadas en Facebook, Instagram, X y Threads, más publicación asistida en Nextdoor y TikTok.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-publication-button" onClick={openCreate}
              disabled={activeTenant?.status === "suspended"}
              title={activeTenant?.status === "suspended" ? "Cuenta suspendida" : undefined}
              className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
            >
              <Plus className="h-4 w-4 mr-1" /> Nueva publicación
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="publication-dialog">
            <DialogHeader><DialogTitle className="font-heading">Nueva publicación</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Cuentas destino</Label>
                {accounts.filter((a) => a.connection_status === "connected").length === 0 && (
                  <p className="text-xs text-[#EF4444]">No hay cuentas conectadas. Conectá una en Conexiones primero.</p>
                )}
                <div className="space-y-1">
                  {accounts.map((a) => {
                    const Icon = PLATFORM_ICON[a.platform] || Facebook;
                    const disabled = a.connection_status !== "connected";
                    return (
                      <label key={a.id} className={`flex items-center gap-2 p-2 border border-[#E4E4E7] rounded-sm ${disabled ? "opacity-40" : "cursor-pointer"}`} data-testid={`publication-account-${a.id}`}>
                        <Checkbox disabled={disabled} checked={form.account_ids.includes(a.id)} onCheckedChange={() => toggleAccount(a.id)} />
                        <Icon className="h-4 w-4 text-[#71717A]" />
                        <span className="text-sm">{a.display_name}</span>
                        {ASSISTED_PLATFORMS.has(a.platform) && (
                          <span className="text-[10px] uppercase tracking-wider bg-[#8ED500]/15 text-[#4D7A00] px-1.5 py-0.5 rounded-sm cursor-help" title={ASSISTED_TOOLTIP}>
                            Asistida
                          </span>
                        )}
                        {disabled && <span className="text-[10px] text-[#EF4444] ml-auto">desconectada</span>}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex justify-between">
                  <Label className="text-xs text-[#71717A]">Texto</Label>
                  {activeLimit && (
                    <span className={`text-xs ${form.text.length > activeLimit ? "text-[#EF4444]" : "text-[#71717A]"}`} data-testid="char-counter">
                      {form.text.length}/{activeLimit}
                    </span>
                  )}
                </div>
                <Textarea data-testid="publication-text-input" rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
                {showXLinkCostWarning && (
                  <div className="flex items-center gap-1.5 text-xs text-[#F59E0B]" data-testid="x-link-cost-warning">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Este post incluye un enlace. En X, publicar con enlace cuesta más que sin enlace.
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Imagen {needsImage && "(obligatoria para Instagram)"}</Label>
                <MediaPicker selected={form.media} onSelect={(id) => setForm({ ...form, media: id })} />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Programar para (hora local del tenant{activeTenant?.timezone ? ` — ${activeTenant.timezone}` : ""})</Label>
                <Input data-testid="publication-schedule-input" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
                {selectedAccounts.some((a) => a.platform === "nextdoor") && (
                  <p className="text-[10px] text-[#4D7A00]" data-testid="nextdoor-assisted-hint">
                    Nextdoor es asistida: esta fecha es tu objetivo para publicarla vos con el botón "Publicar en Nextdoor".
                    Si se pasa sin que la confirmes o la descartes, queda marcada como vencida.
                  </p>
                )}
                {selectedAccounts.some((a) => a.platform === "tiktok") && (
                  <p className="text-[10px] text-[#4D7A00]" data-testid="tiktok-assisted-hint">
                    TikTok es asistida: en esta fecha se entrega el contenido al buzón de notificaciones de la app
                    (nunca antes) y lo terminás de publicar vos con un toque. El link no es clicable en la descripción --
                    pegalo en la bio de tu cuenta para que sirva.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Destino del link</Label>
                <Select value={form.destination_type} onValueChange={(v) => setForm({ ...form, destination_type: v })}>
                  <SelectTrigger data-testid="publication-destination-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">URL externa</SelectItem>
                    <SelectItem value="lead_form">Formulario de leads</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.destination_type === "external" && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Link de destino (tracking)</Label>
                  <Input data-testid="publication-destination-input" placeholder="https://tuempresa.com/promo" value={form.destination_url} onChange={(e) => setForm({ ...form, destination_url: e.target.value })} />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                data-testid="save-publication-button" onClick={save}
                disabled={activeTenant?.status === "suspended"}
                className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
              >Programar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {publisherConfig && (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-4" data-testid="publisher-config-card">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3">
              <Bot className="h-5 w-5 text-[#0055FF] mt-0.5" />
              <div>
                <p className="font-heading font-bold text-sm text-[#09090B]">Programador automático</p>
                <p className="text-xs text-[#71717A] max-w-md">
                  Toma contenido ya aprobado y lo programa solo, repartido en la franja horaria elegida.
                  Nunca aprueba ni genera contenido nuevo.
                </p>
                {publisherConfig.assisted_status?.filter((s) => s.blocked).map((s) => (
                  <p key={s.platform} className="text-xs text-[#EF4444] mt-1" data-testid={`assisted-cap-warning-${s.platform}`}>
                    {s.account_display_name} tiene {s.pending_count} publicación(es) esperando que las publiques vos.
                  </p>
                ))}
                {publisherConfig.assisted_status?.filter((s) => s.tiktok_uploads_capped).map((s) => (
                  <p key={`tiktok-upload-cap-${s.platform}`} className="text-xs text-[#EF4444] mt-1" data-testid="assisted-cap-warning-tiktok-uploads">
                    TikTok tiene {s.tiktok_pending_uploads_24h} publicación(es) esperando que las termines en la app.
                  </p>
                ))}
                {publisherConfig.last_run_at && (
                  <p className="text-xs text-[#71717A] mt-1" data-testid="publisher-config-last-run">
                    Última corrida: {formatTenantDateTime(publisherConfig.last_run_at, activeTenant?.timezone)} — {runResultLabel(publisherConfig.last_run_result)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {user?.is_platform_operator && (
                <Button
                  variant="outline" size="sm" className="h-8 rounded-md text-xs"
                  onClick={runPublisherNow} disabled={runningNow} data-testid="publisher-config-run-now-button"
                >
                  {runningNow ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Correr ahora"}
                </Button>
              )}
              <Switch
                checked={publisherConfig.enabled}
                onCheckedChange={(v) => setPublisherConfig({ ...publisherConfig, enabled: v })}
                data-testid="publisher-config-enabled-switch"
              />
              <span className="text-xs" data-testid="publisher-config-status-label">{publisherConfig.enabled ? "Activo" : "Apagado"}</span>
            </div>
          </div>
          <div className="flex items-end gap-4 flex-wrap mt-4">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-[#71717A]">Piezas por día</Label>
              <Input
                type="number" min={1} max={20} value={publisherConfig.posts_per_day}
                onChange={(e) => setPublisherConfig({ ...publisherConfig, posts_per_day: parseInt(e.target.value, 10) || 1 })}
                className="h-8 w-16 text-xs" data-testid="publisher-config-posts-per-day-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-[#71717A]">Franja</Label>
              <Select value={String(publisherConfig.window_start_hour_local)} onValueChange={(v) => setPublisherConfig({ ...publisherConfig, window_start_hour_local: parseInt(v, 10) })}>
                <SelectTrigger data-testid="publisher-config-window-start-select" className="h-8 w-20 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-xs text-[#71717A]">a</span>
              <Select value={String(publisherConfig.window_end_hour_local)} onValueChange={(v) => setPublisherConfig({ ...publisherConfig, window_end_hour_local: parseInt(v, 10) })}>
                <SelectTrigger data-testid="publisher-config-window-end-select" className="h-8 w-20 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-[#71717A]">Tope asistidas</Label>
              <Input
                type="number" min={1} max={20} value={publisherConfig.assisted_pending_limit}
                onChange={(e) => setPublisherConfig({ ...publisherConfig, assisted_pending_limit: parseInt(e.target.value, 10) || 1 })}
                className="h-8 w-16 text-xs" data-testid="publisher-config-assisted-limit-input"
              />
            </div>
            <div className="flex items-center gap-1">
              {DAY_LABELS.map((label, idx) => (
                <button
                  key={label} type="button" onClick={() => toggleDay(idx)}
                  data-testid={`publisher-config-day-${idx}`}
                  className={`h-8 w-9 text-[10px] rounded-sm border ${publisherConfig.days_of_week.includes(idx) ? "bg-[#0055FF] text-white border-[#0055FF]" : "border-[#E4E4E7] text-[#71717A]"}`}
                >{label}</button>
              ))}
            </div>
            <Button
              size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md h-8"
              onClick={savePublisherConfig} disabled={savingPublisherConfig} data-testid="publisher-config-save-button"
            >
              {savingPublisherConfig ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Guardar"}
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <Label className="text-xs text-[#71717A]">Redes</Label>
            {[...new Set(accounts.filter((a) => a.connection_status === "connected").map((a) => a.platform))].map((platform) => (
              <button
                key={platform} type="button" onClick={() => togglePlatform(platform)}
                data-testid={`publisher-config-platform-${platform}`}
                className={`px-2 h-7 text-[10px] rounded-sm border capitalize ${publisherConfig.platforms.includes(platform) ? "bg-[#0055FF] text-white border-[#0055FF]" : "border-[#E4E4E7] text-[#71717A]"}`}
              >{platform}</button>
            ))}
            <span className="text-[10px] text-[#71717A]" data-testid="publisher-config-platforms-hint">
              {publisherConfig.platforms.length === 0 ? "Ninguna marcada = Todas las conectadas" : "Solo las redes marcadas"}
            </span>
          </div>
        </Card>
      )}

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        {needsRescheduleCount > 0 && (
          <div
            data-testid="needs-reschedule-banner"
            className="flex items-start gap-3 border-b border-[#F59E0B]/30 bg-[#F59E0B]/10 p-3 text-xs text-[#92400E]"
          >
            <AlertTriangle className="h-4 w-4 text-[#F59E0B] shrink-0 mt-0.5" />
            <span>
              <strong>{needsRescheduleCount} publicación(es)</strong> quedaron pendientes de reprogramar
              (la fecha programada pasó mientras la cuenta estaba suspendida). No se publican solas: reprogramalas o cancelalas.
            </span>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center px-6" data-testid="publications-empty-state">
            <CalendarClock className="h-8 w-8 text-[#A1A1AA]" />
            <p className="mt-3 text-sm font-medium text-[#09090B]">No hay publicaciones programadas todavía</p>
            <p className="mt-1 text-xs text-[#71717A] max-w-sm">
              Programá una publicación directa acá, o generá contenido primero y enviala desde ahí a tus
              redes conectadas.
            </p>
            <Button
              size="sm" className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
              data-testid="publications-empty-state-button" onClick={openCreate}
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Nueva publicación
            </Button>
          </div>
        ) : (
          <Table data-testid="publications-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Cuenta</TableHead>
                <TableHead>Texto</TableHead>
                <TableHead>Programada</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => {
                const Icon = PLATFORM_ICON[p.platform] || Facebook;
                const s = STATUS[p.status] || STATUS.draft;
                return (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => { setDetailPubId(p.id); setDetailOpen(true); }} data-testid={`publication-row-${p.id}`}>
                    <TableCell className="flex items-center gap-2"><Icon className="h-4 w-4 text-[#71717A]" /> {p.account_display_name}</TableCell>
                    <TableCell className="max-w-xs truncate text-[#71717A]">{p.text}</TableCell>
                    <TableCell className="text-[#71717A]" data-testid={`publication-scheduled-${p.id}`}>
                      {formatTenantDateTime(p.scheduled_at, activeTenant?.timezone)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge
                          className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${s.cls} ${s.tooltip ? "cursor-help" : ""}`}
                          title={s.tooltip} data-testid={`publication-status-${p.id}`}
                        >
                          {s.label}
                        </Badge>
                        {p.auto_scheduled && (
                          <Badge className="border-0 text-[10px] uppercase tracking-wider rounded-sm bg-[#0055FF]/10 text-[#0055FF]" data-testid={`publication-auto-badge-${p.id}`} title="Programada automáticamente">
                            <Bot className="h-3 w-3 mr-1 inline" />Auto
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {["draft", "needs_reschedule"].includes(p.status) && (
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openScheduleDraft(p); }} data-testid={`schedule-draft-${p.id}`} className="text-[#0055FF]">
                          <CalendarClock className="h-4 w-4" />
                        </Button>
                      )}
                      {["draft", "scheduled", "needs_reschedule"].includes(p.status) && (
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); cancel(p); }} data-testid={`cancel-publication-${p.id}`} className="text-[#EF4444]">
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                      {["pending_manual", "expired_unpublished"].includes(p.status) && (
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {p.platform === "nextdoor" && (
                            <Button
                              asChild variant="outline" size="sm" className="h-7 rounded-sm text-xs"
                              data-testid={`publish-nextdoor-${p.id}`}
                            >
                              <a href={p.nextdoor_share_url} target="_blank" rel="noreferrer">
                                <ExternalLink className="h-3 w-3 mr-1" /> Publicar en Nextdoor
                              </a>
                            </Button>
                          )}
                          {p.platform === "tiktok" && (
                            <span
                              className={`text-[10px] ${p.assisted_delivered_at ? "text-[#4D7A00]" : "text-[#71717A]"}`}
                              data-testid={`tiktok-delivery-status-${p.id}`}
                              title={p.failure_reason || undefined}
                            >
                              {p.assisted_delivered_at ? (
                                <><Send className="h-3 w-3 inline mr-1" /> Enviado al buzón</>
                              ) : p.failure_reason ? (
                                <span className="text-[#EF4444]">No se pudo enviar</span>
                              ) : (
                                "Esperando su hora"
                              )}
                            </span>
                          )}
                          <Button
                            variant="ghost" size="icon" className="text-[#10B981]" title="Ya la publiqué"
                            onClick={() => markPublishedManual(p)} data-testid={`mark-published-manual-${p.id}`}
                          >
                            <CheckCheck className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="text-[#EF4444]" title="Descartar"
                            onClick={() => discardManual(p)} data-testid={`discard-manual-${p.id}`}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <PublicationDetailSheet publicationId={detailPubId} open={detailOpen} onOpenChange={setDetailOpen} />

      <Dialog open={!!scheduleDraft} onOpenChange={(v) => !v && setScheduleDraft(null)}>
        <DialogContent className="max-w-lg" data-testid="schedule-draft-dialog">
          <DialogHeader><DialogTitle className="font-heading">Programar publicación</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#71717A]">Texto</Label>
              <Textarea data-testid="schedule-draft-text-input" rows={4} value={scheduleDraft?.text || ""}
                onChange={(e) => setScheduleDraft((d) => ({ ...d, text: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#71717A]">Programar para (hora local del tenant{activeTenant?.timezone ? ` — ${activeTenant.timezone}` : ""})</Label>
              <Input data-testid="schedule-draft-datetime-input" type="datetime-local" value={scheduleDraft?.scheduled_at || ""}
                onChange={(e) => setScheduleDraft((d) => ({ ...d, scheduled_at: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="confirm-schedule-draft-button" onClick={saveScheduleDraft}
              disabled={activeTenant?.status === "suspended"}
              className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
            >Programar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
